import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Fab
} from '@mui/material';
import {
  Close as CloseIcon,
  Alarm as AlarmIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Snooze as SnoozeIcon,
  CheckCircle as CompleteIcon,
  Repeat as RepeatIcon,
  FilterList as FilterIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import {
  Reminder,
  ReminderFilters,
  todoService,
  CreateReminderDTO,
  UpdateReminderDTO
} from '../../services/api/todo.service';
import ReminderDialog from './ReminderDialog';

interface ReminderListDialogProps {
  open: boolean;
  onClose: () => void;
  taskId?: string;
  conversationId?: string;
}

const ReminderListDialog: React.FC<ReminderListDialogProps> = ({
  open,
  onClose,
  taskId,
  conversationId
}) => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ReminderFilters>({});
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(null);

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const appliedFilters = {
        ...filters,
        ...(conversationId && { conversation_id: conversationId }),
        ...(taskId && { task_id: taskId })
      };

      const data = await todoService.getReminders(appliedFilters);
      setReminders(data);
    } catch (err) {
      console.error('Error fetching reminders:', err);
      setError('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, [filters, conversationId, taskId]);

  useEffect(() => {
    if (open) {
      fetchReminders();
    }
  }, [open, fetchReminders]);

  const handleCreateReminder = async (reminderData: CreateReminderDTO) => {
    try {
      await todoService.createReminder(reminderData);
      setShowCreateDialog(false);
      fetchReminders();
    } catch (err) {
      console.error('Error creating reminder:', err);
      setError('Failed to create reminder');
    }
  };

  const handleUpdateReminder = async (reminderData: UpdateReminderDTO) => {
    if (!editingReminder) return;

    try {
      await todoService.updateReminder(editingReminder.id, reminderData);
      setEditingReminder(null);
      fetchReminders();
    } catch (err) {
      console.error('Error updating reminder:', err);
      setError('Failed to update reminder');
    }
  };

  const handleSaveReminder = async (reminderData: CreateReminderDTO | UpdateReminderDTO) => {
    if (editingReminder) {
      await handleUpdateReminder(reminderData as UpdateReminderDTO);
    } else {
      await handleCreateReminder(reminderData as CreateReminderDTO);
    }
  };

  const handleDeleteReminder = async (reminderId: string) => {
    try {
      await todoService.deleteReminder(reminderId);
      fetchReminders();
      handleCloseActionMenu();
    } catch (err) {
      console.error('Error deleting reminder:', err);
      setError('Failed to delete reminder');
    }
  };

  const handleSnoozeReminder = async (reminderId: string, minutes: number) => {
    try {
      await todoService.snoozeReminder(reminderId, minutes);
      fetchReminders();
      handleCloseActionMenu();
    } catch (err) {
      console.error('Error snoozing reminder:', err);
      setError('Failed to snooze reminder');
    }
  };

  const handleCompleteReminder = async (reminderId: string) => {
    try {
      await todoService.completeReminder(reminderId);
      fetchReminders();
      handleCloseActionMenu();
    } catch (err) {
      console.error('Error completing reminder:', err);
      setError('Failed to complete reminder');
    }
  };

  const handleActionMenu = (event: React.MouseEvent<HTMLElement>, reminder: Reminder) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedReminder(reminder);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedReminder(null);
  };

  const handleFilterChange = (field: keyof ReminderFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (reminder: Reminder) => {
    if (reminder.status === 'completed') return 'success';
    if (reminder.status === 'cancelled') return 'default';
    if (isPast(new Date(reminder.reminder_time))) return 'error';
    return 'primary';
  };

  const getStatusText = (reminder: Reminder) => {
    if (reminder.status === 'completed') return 'Completed';
    if (reminder.status === 'cancelled') return 'Cancelled';
    if (isPast(new Date(reminder.reminder_time))) return 'Overdue';
    return 'Active';
  };

  const getReminderIcon = (reminder: Reminder) => {
    if (reminder.type === 'recurring') return <RepeatIcon fontSize="small" />;
    return <AlarmIcon fontSize="small" />;
  };

  const overdue = reminders.filter(r => r.status === 'active' && isPast(new Date(r.reminder_time))).length;
  const active = reminders.filter(r => r.status === 'active').length;
  const completed = reminders.filter(r => r.status === 'completed').length;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Badge badgeContent={overdue} color="error">
              <AlarmIcon color="primary" />
            </Badge>
            <Typography variant="h6">
              Reminders {taskId ? '(Task)' : conversationId ? '(Conversation)' : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              color={showFilters ? 'primary' : 'default'}
            >
              <FilterIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {/* Stats */}
            <Paper style={{ padding: 16, backgroundColor: '#fafafa' }}>
              <Stack direction="row" spacing={3}>
                <div style={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">{active}</Typography>
                  <Typography variant="caption">Active</Typography>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error">{overdue}</Typography>
                  <Typography variant="caption">Overdue</Typography>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Typography variant="h6" style={{ color: '#2e7d32' }}>{completed}</Typography>
                  <Typography variant="caption">Completed</Typography>
                </div>
              </Stack>
            </Paper>

            {/* Filters */}
            {showFilters && (
              <Paper style={{ padding: 16, backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Filters
                </Typography>
                <Stack direction="row" spacing={2} style={{ marginBottom: 16 }}>
                  <FormControl size="small" style={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status?.[0] || ''}
                      onChange={(e) => handleFilterChange('status', e.target.value ? [e.target.value] : undefined)}
                      label="Status"
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" style={{ minWidth: 120 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type?.[0] || ''}
                      onChange={(e) => handleFilterChange('type', e.target.value ? [e.target.value] : undefined)}
                      label="Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="one_time">One Time</MenuItem>
                      <MenuItem value="recurring">Recurring</MenuItem>
                      <MenuItem value="conditional">Conditional</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" style={{ minWidth: 120 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={filters.priority?.[0] || ''}
                      onChange={(e) => handleFilterChange('priority', e.target.value ? [e.target.value] : undefined)}
                      label="Priority"
                    >
                      <MenuItem value="">All Priorities</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Button size="small" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </Paper>
            )}

            {/* Error */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Loading */}
            {loading && (
              <div style={{ textAlign: 'center', paddingTop: 24, paddingBottom: 24 }}>
                <Typography>Loading reminders...</Typography>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && reminders.length === 0 && (
              <Alert severity="info">
                No reminders found. Click the + button to create your first reminder.
              </Alert>
            )}

            {/* Reminders List */}
            {!loading && !error && reminders.length > 0 && (
              <List style={{ maxHeight: 400, overflow: 'auto' }}>
                {reminders.map((reminder, index) => (
                  <React.Fragment key={reminder.id}>
                    <ListItem style={{ alignItems: 'flex-start' }}>
                      <ListItemIcon style={{ marginTop: 8 }}>
                        {getReminderIcon(reminder)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2">
                              {reminder.title}
                            </Typography>
                            <Chip
                              label={getStatusText(reminder)}
                              size="small"
                              color={getStatusColor(reminder) as any}
                              variant="outlined"
                            />
                            <Chip
                              label={reminder.priority}
                              size="small"
                              color={getPriorityColor(reminder.priority) as any}
                              variant="filled"
                            />
                            {reminder.type === 'recurring' && (
                              <Chip
                                label="Recurring"
                                size="small"
                                icon={<RepeatIcon fontSize="small" />}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Stack spacing={1} style={{ marginTop: 8 }}>
                            {reminder.description && (
                              <Typography variant="body2" color="text.secondary">
                                {reminder.description}
                              </Typography>
                            )}

                            <Stack direction="row" spacing={2} alignItems="center">
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ScheduleIcon fontSize="small" color="disabled" />
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(reminder.reminder_time), 'MMM dd, yyyy at HH:mm')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({formatDistanceToNow(new Date(reminder.reminder_time), { addSuffix: true })})
                                </Typography>
                              </div>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                              {reminder.notification_channels.map(channel => (
                                <Chip
                                  key={channel}
                                  label={channel}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </Stack>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={(e) => handleActionMenu(e, reminder)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>

                    {index < reminders.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>

        {/* FAB for Create */}
        <Fab
          color="primary"
          style={{ position: 'absolute', bottom: 16, right: 16 }}
          onClick={() => setShowCreateDialog(true)}
        >
          <AddIcon />
        </Fab>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={() => {
          setEditingReminder(selectedReminder);
          handleCloseActionMenu();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>

        {selectedReminder?.status === 'active' && (
          <>
            <MenuItem onClick={() => selectedReminder && handleCompleteReminder(selectedReminder.id)}>
              <ListItemIcon><CompleteIcon fontSize="small" /></ListItemIcon>
              Complete
            </MenuItem>
            <MenuItem onClick={() => selectedReminder && handleSnoozeReminder(selectedReminder.id, 15)}>
              <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
              Snooze 15 min
            </MenuItem>
            <MenuItem onClick={() => selectedReminder && handleSnoozeReminder(selectedReminder.id, 60)}>
              <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
              Snooze 1 hour
            </MenuItem>
          </>
        )}

        <Divider />
        <MenuItem
          onClick={() => selectedReminder && handleDeleteReminder(selectedReminder.id)}
          style={{ color: '#d32f2f' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <ReminderDialog
        open={showCreateDialog || !!editingReminder}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingReminder(null);
        }}
        onSave={handleSaveReminder}
        existingReminder={editingReminder}
        taskId={taskId}
        conversationId={conversationId}
      />
    </>
  );
};

export default ReminderListDialog;